import { create } from 'zustand'
import { PanelHistoryItemWithDelimiter, PanelHistoryItem } from './types'
import { Stack } from './utils/stack'
import { Listeners } from './listeners'

type SectionType = 'INIT' | string

interface IStore {
  panelsHistory: Record<string, Stack<PanelHistoryItemWithDelimiter>>
  sectionsHistory: Record<string, Stack<SectionType>>
  activeSectionsHistory: Stack<string>
  panelToSectionMap: Record<string, string>
  panelParentMap: Record<string, string>
}

const initialState: IStore = {
  panelsHistory: {
    root: new Stack(false),
  },
  sectionsHistory: {},
  activeSectionsHistory: new Stack(),
  panelToSectionMap: {},
  panelParentMap: {},
}

export const useRouterStore = create<IStore>()(() => initialState)

export class Store {
  private static panelsHistory: IStore['panelsHistory']
  private static sectionsHistory: IStore['sectionsHistory']
  private static activeSectionsHistory: IStore['activeSectionsHistory']
  private static panelToSectionMap: IStore['panelToSectionMap']
  private static panelParentMap: IStore['panelParentMap']

  static createPanel(name: string, initialItem?: PanelHistoryItem) {
    if (!!this.panelsHistory[name]) return

    this.panelsHistory[name] = new Stack<PanelHistoryItemWithDelimiter>()
    if (initialItem) this.panelsHistory[name].push(initialItem)

    useRouterStore.setState(this.get())
  }

  static createSection(name: string) {
    if (!!this.sectionsHistory[name]) return

    this.sectionsHistory[name] = new Stack<string>()
    useRouterStore.setState(this.get())
  }

  static setSectionOfPanel(panel: string, section: string) {
    this.panelToSectionMap[panel] = section
    useRouterStore.setState(this.get())
  }

  static setPanelParent(panel: string, parent: string) {
    const parentOfParent = this.panelParentMap[parent]
    this.panelParentMap[panel] = parentOfParent || parent
    useRouterStore.setState(this.get())
  }

  static getPanelChildPanels(parent: string) {
    return Object.keys(this.panelParentMap).filter(
      child => this.panelParentMap[child] === parent,
    )
  }

  static getPanelHistory(panel: string) {
    return this.panelsHistory[panel]
  }

  static getSectionHistory(section: string) {
    if (!section) return undefined
    if (!this.sectionsHistory[section]) this.createSection(section)

    return this.sectionsHistory[section]
  }

  static getActiveSectionsHistory() {
    return this.activeSectionsHistory
  }

  static getSectionOfPanel(panel: string) {
    return this.panelToSectionMap[panel]
  }

  static getPanelParent(panel: string) {
    return this.panelParentMap[panel]
  }

  static get(): IStore {
    return {
      panelsHistory: this.panelsHistory,
      sectionsHistory: this.sectionsHistory,
      activeSectionsHistory: this.activeSectionsHistory,
      panelToSectionMap: this.panelToSectionMap,
      panelParentMap: this.panelParentMap,
    }
  }

  static commit() {
    useRouterStore.setState(this.get())
  }

  static initiate() {
    this.panelsHistory = initialState.panelsHistory
    this.sectionsHistory = initialState.sectionsHistory
    this.activeSectionsHistory = initialState.activeSectionsHistory
    this.panelToSectionMap = initialState.panelToSectionMap
    this.panelParentMap = initialState.panelParentMap

    useRouterStore.setState(initialState)
    Listeners.listen(() => {
      useRouterStore.setState(this.get())
    })
  }
}

Store.initiate()

window.addEventListener('keyup', e => {
  if (e.code === 'KeyS') {
    console.log('store:', useRouterStore.getState())
  }
})
